import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  cartItems: [],
  quantity: 0,
  totalPrice: 0,
  status: "idle",
};

const getSelectedVariant = (product) =>
  product.selectedVariant || product.variants?.[0] || null;

const getCartKey = (product) => {
  const variant = getSelectedVariant(product);
  return `${product.id}-${variant?.id || "default"}`;
};

const getUnitPrice = (product) => Number(getSelectedVariant(product)?.price || 0);

const matchesCartItem = (item, key) =>
  String(item.cartKey || getCartKey(item)) === String(key) ||
  String(item.id) === String(key);

const recalculateCart = (state) => {
  state.quantity = state.cartItems.reduce((acc, item) => acc + item.quantity, 0);
  state.totalPrice = state.cartItems.reduce(
    (acc, item) => acc + item.totalPrice,
    0,
  );
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const product = action.payload;
      const cartKey = getCartKey(product);
      const selectedVariant = getSelectedVariant(product);
      const unitPrice = getUnitPrice(product);
      const existing = state.cartItems.find((item) =>
        matchesCartItem(item, cartKey),
      );
      const incomingQty = product.quantity || 1;

      if (existing) {
        existing.quantity += incomingQty;
        existing.totalPrice = existing.unitPrice * existing.quantity;
      } else {
        state.cartItems.push({
          ...product,
          cartKey,
          selectedVariant,
          variantTitle:
            selectedVariant?.title && selectedVariant.title !== "Default Title"
              ? selectedVariant.title
              : "",
          unitPrice,
          quantity: incomingQty,
          totalPrice: unitPrice * incomingQty,
        });
      }

      recalculateCart(state);
    },

    removeFromCart: (state, action) => {
      state.cartItems = state.cartItems.filter(
        (item) => !matchesCartItem(item, action.payload),
      );
      recalculateCart(state);
    },

    increaseQuantity: (state, action) => {
      const item = state.cartItems.find((item) =>
        matchesCartItem(item, action.payload),
      );
      if (item) {
        item.quantity += 1;
        item.unitPrice = item.unitPrice || getUnitPrice(item);
        item.totalPrice = item.unitPrice * item.quantity;
      }
      recalculateCart(state);
    },

    decreaseQuantity: (state, action) => {
      const item = state.cartItems.find((item) =>
        matchesCartItem(item, action.payload),
      );
      if (item && item.quantity > 1) {
        item.quantity -= 1;
        item.unitPrice = item.unitPrice || getUnitPrice(item);
        item.totalPrice = item.unitPrice * item.quantity;
      } else if (item && item.quantity === 1) {
        state.cartItems = state.cartItems.filter(
          (i) => !matchesCartItem(i, action.payload),
        );
      }
      recalculateCart(state);
    },

    clearCart: (state) => {
      state.cartItems = [];
      state.quantity = 0;
      state.totalPrice = 0;
      state.status = "idle";
    },

    setStatus: (state, action) => {
      state.status = action.payload;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  increaseQuantity,
  decreaseQuantity,
  clearCart,
  setStatus,
} = cartSlice.actions;

export default cartSlice.reducer;
